import React, { useMemo } from "react";
import { Button } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { InvestigationFormWrapper } from "./InvestigationForm.styles";

/**
 * Q1 — pre-Brett-02062026 Factual records are frozen read-only. They were
 * captured against the simpler legacy schema (incidentSummary, keyFindings,
 * free-text correctiveActions) and Brett's spec deliberately did not require
 * a migration into the new structured form. We surface what was captured at
 * the time without offering edit affordances, plus a banner explaining why.
 */
const safeParse = (content) => {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === "object") return parsed;
    return { incidentSummary: content };
  } catch {
    return { incidentSummary: content };
  }
};

const LegacyFactualReadOnly = ({ incident, investigation, onBack }) => {
  const parsed = useMemo(() => safeParse(investigation?.content), [investigation?.content]);
  const incidentSummary = parsed?.incidentSummary || "";
  const keyFindings = parsed?.keyFindings || "";
  const correctiveActions = Array.isArray(parsed?.correctiveActions)
    ? parsed.correctiveActions
    : [];

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationFormWrapper>
          <div className="inv-header">
            <div>
              <button className="inv-back-button" onClick={onBack}>
                <ArrowLeftOutlined /> Back to Incidents
              </button>
              <h1 className="inv-title">Factual Investigation (Legacy)</h1>
              <p className="inv-subtitle">
                {incident?.report_number} — {incident?.description || ""}
              </p>
            </div>
          </div>

          <div
            className="readonly-notice"
            style={{
              background: "#fffbeb",
              borderColor: "#fde68a",
              color: "#92400e",
              padding: "12px 16px",
              borderRadius: 8,
              marginBottom: 16,
              borderWidth: 1,
              borderStyle: "solid",
            }}
          >
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            This Factual Investigation was created against the previous (simpler)
            form. It is preserved here read-only — new investigations created
            from this point onward use the expanded Factual Investigation form
            per the updated specification.
          </div>

          <div className="form-card">
            <h3 style={sectionHeading}>Incident Summary</h3>
            <p style={readonlyText}>{incidentSummary || <em>(not provided)</em>}</p>

            <h3 style={sectionHeading}>Key Findings</h3>
            <p style={readonlyText}>{keyFindings || <em>(not provided)</em>}</p>

            <h3 style={sectionHeading}>Corrective Actions</h3>
            {correctiveActions.length === 0 ? (
              <p style={readonlyText}>
                <em>No corrective actions recorded.</em>
              </p>
            ) : (
              <ol style={{ paddingLeft: 20 }}>
                {correctiveActions.map((ca, idx) => {
                  if (typeof ca === "string") {
                    return (
                      <li key={idx} style={{ marginBottom: 8 }}>
                        {ca || <em>(empty)</em>}
                      </li>
                    );
                  }
                  return (
                    <li key={idx} style={{ marginBottom: 8 }}>
                      <strong>{ca.action || ca.description || "(no description)"}</strong>
                      {ca.assignedTo ? <> · Assignee #{ca.assignedTo}</> : null}
                      {ca.dueDate ? <> · Due {ca.dueDate}</> : null}
                    </li>
                  );
                })}
              </ol>
            )}

            {investigation?.review_comment && (
              <>
                <h3 style={sectionHeading}>Review Comment</h3>
                <p style={readonlyText}>{investigation.review_comment}</p>
              </>
            )}

            <div style={{ marginTop: 24 }}>
              <Button onClick={onBack}>Back to Incidents</Button>
            </div>
          </div>
        </InvestigationFormWrapper>
      </Box>
    </LayoutWrapper>
  );
};

const sectionHeading = {
  fontSize: 16,
  fontWeight: 600,
  color: "#101828",
  marginTop: 20,
  marginBottom: 8,
};

const readonlyText = {
  whiteSpace: "pre-wrap",
  background: "#f9fafb",
  padding: 12,
  borderRadius: 6,
  border: "1px solid #e5e7eb",
  color: "#1f2937",
};

export default LegacyFactualReadOnly;
